import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { PHOTO_PREVIEW_TRANSFORMS } from '../../../../shared/photoPreview.js';
import { getSupabaseClient } from '@/data/supabase';
import { PHOTO_STALE_TIME } from '@/data/photoQueries';
import useNearViewport from '../../hooks/useNearViewport';
import useSessionStore from '../../state/session';
import PreviewImage from './PreviewImage';

// Bound signing across galleries, so a long conversation cannot flood Storage.
let active = 0;
const waiting: Array<() => void> = [];
async function withSigningSlot<T,>(work: () => Promise<T>): Promise<T> {
  if (active >= 4) await new Promise<void>(resolve => waiting.push(resolve));
  else active += 1;
  try { return await work(); }
  finally {
    const next = waiting.shift();
    if (next) next();
    else active -= 1;
  }
}

type Props = {
  bucket: string;
  storagePath: string;
  name: string;
  isImage?: boolean;
  variant?: 'thumb' | 'card';
  expiresIn?: number;
  imageStyle?: React.CSSProperties;
  onOpen?: (url: string) => void;
};

export default function SignedStorageAttachment({ bucket, storagePath, name, isImage = true,
  variant = 'card', expiresIn = 3600, imageStyle, onOpen }: Props) {
  const { ref, nearViewport } = useNearViewport();
  const principalEpoch = useSessionStore(state => state.principalEpoch);
  const userId = useSessionStore(state => state.session?.user.id);
  const renewAfterMs = Math.max(1000, Math.min(50 * 60 * 1000, (expiresIn - 60) * 1000));
  const query = useQuery({
    queryKey: ['signed-photo-preview', userId, principalEpoch, bucket, storagePath, isImage, variant, expiresIn],
    enabled: nearViewport && Boolean(storagePath),
    staleTime: Math.min(PHOTO_STALE_TIME, renewAfterMs),
    // Retain each workflow's existing link lifetime and renew before expiry.
    refetchInterval: renewAfterMs,
    queryFn: async () => {
      const storage = getSupabaseClient().storage.from(bucket);
      const [original, preview] = await Promise.all([
        withSigningSlot(() => storage.createSignedUrl(storagePath, expiresIn)),
        isImage ? withSigningSlot(() => storage.createSignedUrl(storagePath, expiresIn,
          { transform: PHOTO_PREVIEW_TRANSFORMS[variant] })) : Promise.resolve(null),
      ]);
      if (original.error || !original.data?.signedUrl) throw original.error || new Error('The attachment link could not be loaded.');
      return { original: original.data.signedUrl, preview: preview?.data?.signedUrl };
    },
  });
  const urls = query.data;
  return <div ref={ref} style={{ minHeight: isImage ? (variant === 'thumb' ? 44 : 80) : 20, maxWidth: '100%' }}>
    {urls ? <a href={urls.original} target="_blank" rel="noreferrer" title={`Open ${name}`}
      onClick={onOpen ? event => { event.preventDefault(); onOpen(urls.original); } : undefined}>
      {isImage ? <PreviewImage previewUrl={urls.preview} originalUrl={urls.original} alt={name}
        width={variant === 'thumb' ? 96 : 640} height={variant === 'thumb' ? 96 : 480}
        style={imageStyle ?? { maxWidth: '100%', height: 'auto', maxHeight: 110, objectFit: 'contain', borderRadius: 4 }} /> : name}
    </a> : query.isError ? <span role="alert" className="text-xxs">
      Could not load attachment. <button type="button" className="btn xs subtle" onClick={() => void query.refetch()}>Try again</button>
    </span> : <span className="text-xxs fg-muted">Loading preview...</span>}
  </div>;
}
